import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup, FormBuilder, FormArray, FormControl } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { CustomUtils, MessServiceService } from '@app/shared/common';
import { Subscription, Subject } from 'rxjs';

@Component({
  selector: 'app-file-upload-v2',
  templateUrl: './file-upload-v2.component.html',
})
export class FileUploadV2Component {
  /**
   * * General
   * @CustomUtils Shared custom utils
   * @subscriptions Subscriptions array
   * @appConfig App config from ngrx store
   */
  CustomUtils = CustomUtils;
  subscriptions: Subscription[] = [];
  appConfig: any;
  /**
   * * Observalbes
   * @imageDeleted Store last deleted image name
   */
  imageDeleted: Subject<any> = new Subject<any>();
  /**
   * * Inputs
   * @fileList List of uploaded files
   * @maxFiles Max number of files that can be uploaded
   * @includeSeo Show/hide SEO optimization for images
   * @uploadFormField
   * @controlName Name of control that hold all images data (SEO & image names)
   * @fileType Type of files that can be uploaded
   * @instanceIndex Instance index, needed when you have more than one upload component on same page
   * @fileSize Max file size that can be uploaded (in MB)
   * @formGroup Form group that hold @controlName
   * @lang List of all langs that your app have [{lang:'English', code:'en'}...]
   * @helpMessage This message will be displayed below upload component
   * @multiple Define can you upload multiple files true/false
   * @showImagesPreview You can hide uploaded image preview
   * @showMediaLibrary Show/hide media library
   * @sortable Define can you reorder items in upload component
   * @label Label for upload
   */
  @Input() fileList: any[];
  @Input() maxFiles: number;
  @Input() includeSeo = false;
  @Input() uploadFormField: any;
  @Input() controlName = 'attachments';
  @Input() fileType: string[] = [
    'jpg',
    'png',
    'jpeg',
    'webp',
    'svg',
    'mp4',
    'mov',
    'avi',
    'zip',
    'rar',
    'docx',
    'pdf',
    'pptx',
    'xlsx',
    'doc',
  ];
  @Input() instanceIndex = 0;
  @Input() fileSize = 8;
  @Input() formGroup: FormGroup;
  @Input() uploadType: 'image' | 'any' = 'image';
  @Input() helpMessage: string;
  @Input() multiple: boolean;
  @Input() showImagesPreview = true;
  @Input() sortable = true;
  @Input() label: string;
  @Input() formControlKey: string;
  @Input() controlType: 'array' | 'control' = 'control';
  @Input() class = '';
  /**
   * * Outputs
   * @handleUpload Emit upload event
   */
  @Output() handleUpload = new EventEmitter<any>();

  constructor(
    private fb: FormBuilder,
    public _d: DomSanitizer,
    private messService: MessServiceService
  ) {}

  /* ====================================
 *             MAIN FEATURES
 ======================================= */

  /**
   * This function is called every time when user upload file
   * @param event This is upload event that hold all files
   */
  uploadFile(event: any) {
    /**
     * Check if the maximum number of files has already been added
     */
    const filterDeletedFiles = this.fileList.filter(
      (file) => file.delete != true
    );
    const uploadedFiles = (event.target as HTMLInputElement).files;
    const currentPlusAddedFiles =
      filterDeletedFiles.length + Array.from(uploadedFiles).length;
    if (
      filterDeletedFiles.length < this.maxFiles &&
      currentPlusAddedFiles <= this.maxFiles
    ) {
      Array.from(uploadedFiles).forEach((file) => {
        /**
         * @renamedFile Remove blank spaces or '-' from file name
         */
        const renamedFile = new File([file], file.name.replace(/\s/g, '-'), {
          type: file.type,
        });
        /**
         * @fileSize Calc file size in MB
         */
        const fileSize = +(file.size / 1024 / 1024).toFixed(0);
        /**
         * @fileExtension Get file extension
         */
        const fileExtension =
          this.fileType[0] === 'any' ? 'any' : file.name.split('.').pop();
        console.log(fileExtension);

        /**
         * Check if uploaded file format is valid
         */
        if (!this.fileType.includes(fileExtension)) {
          this.messService.addWarn({
            severity: 'warn',
            title: 'The file format is not supported!',
            detail: 'The file must be in one of the supported formats.',
          });
          return;
        }
        /**
         * Check if uploaded file size is valid
         */
        if (fileSize > this.fileSize) {
          this.messService.addWarn({
            severity: 'warn',
            title: 'File is too large!',
            detail: `Your file must be smaller than ${fileSize} MB`,
          });
          return;
        }
        /**
         * Push file to @fileList array
         */
        this.fileList.push({
          originalFile: renamedFile,
          imageLocalUrl: window.URL.createObjectURL(renamedFile),
          filename: renamedFile.name,
        });

        if (this.formControlKey) {
          this.createFormFieldsForFile(renamedFile);
        }

        /**
         * Reset file inptu so you can trigger event again if needed
         */
        event.srcElement.value = null;
      });

      this.handleUpload.emit(true);
    } else {
      this.messService.addWarn({
        severity: 'warn',
        title: 'You have added the maximum number of files',
        detail: 'Delete a file',
      });
    }
  }

  /* ====================================
 *                HELPERS
 ======================================= */

  createFormFieldsForFile(file: any) {
    if (!this.includeSeo) {
      if (this.controlType === 'array') {
        this.getFilesFormArray().push(this.createFileDescription(file.name));
      }
      // this.lang?.forEach((singleLang) => {
      //   this.getLangAttachments(singleLang.code).push(
      //     this.createFileDescription(file.name)
      //   );
      // });
      if (this.uploadFormField) {
        this.uploadFormField.push(this.createFileDescription(file.name));
      }
    }
  }

  /**
   * @param langCode Provide language code. example: 'sr', 'en', 'de'...
   * @returns FormGroup with attachments for provided language
   */
  getLangAttachments(langCode: string) {
    return this.formGroup.get(`locale.${langCode}.attachments`) as FormArray;
  }

  /**
   * This function is used for products because product images don`t have SEO, just fileName
   * @param fileName Provide file/image name
   * @returns FromGroup width fileName
   */
  createFileDescription(fileName: string): FormGroup {
    return this.fb.group({
      filename: [fileName],
    });
  }

  /**
   * When you delete file from fileList you also need to delete SEO & name fields from FormGroup
   * @param imgIndex Index fo deleted image
   */
  removeImageDescription(imgIndex: number) {
    // this.getFilesFormArray().removeAt(imgIndex);
    // (this.getFilesFormArray().at(imgIndex) as FormGroup).addControl(
    //   'delete',
    //   new FormControl(true)
    // );
    if (this.maxFiles > 1) {
      (this.getFilesFormArray().at(imgIndex) as FormGroup).addControl(
        'delete',
        new FormControl(true)
      );
    } else {
      this.getFilesFormControl().setValue({
        ...this.getFilesFormControl().value,
        delete: true,
      });
    }

    if (this.uploadFormField) {
      this.uploadFormField.removeAt(imgIndex);
    }
  }

  /**
   * Remove file from file upload component
   * @param event Delete event
   * @param fileIndex File/image index
   * @param fileName File/image name
   */
  removeFile(event: Event, fileIndex: number, fileName: string) {
    // this.fileList.splice(fileIndex, 1);
    this.fileList[fileIndex] = {
      ...this.fileList[fileIndex],
      delete: true,
      originalFile: null,
    };
    if (this.formControlKey) {
      this.removeImageDescription(fileIndex);
    }
    this.imageDeleted.next(fileName);
  }

  cleanURL(oldURL): SafeUrl {
    return this._d.bypassSecurityTrustResourceUrl(oldURL);
  }

  // * TEST
  getFilesFormArray() {
    return this.formGroup.get(this.formControlKey) as FormArray;
  }

  getFilesFormControl() {
    return this.formGroup.get(this.formControlKey) as FormControl;
  }
}
